import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import CallSplitIcon from '@mui/icons-material/CallSplit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import BrandState from '../../components/BrandState'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { useMemo, useState } from 'react'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  filterBarBtnSx,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '@/components/dataList'
import {
  type SaleAmendment,
  useCancelSaleAmendmentMutation,
  useGetSaleAmendmentsQuery,
  useSeparateSaleAmendmentContractMutation,
} from '../../store/api/saleAmendmentsApi'
import type { SaleContractPayload } from '../../store/api/saleContractApi'
import ContractFormDialog from './ContractFormDialog'
import SaleAmendmentFormDialog from './SaleAmendmentFormDialog'
import SaleAmendmentPrintDialog from './SaleAmendmentPrintDialog'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, SHOW_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import ResizeHandle from '../../components/ResizeHandle'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof SaleAmendment
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

// §2.4 / §5: exact column order from Access frmContractSalesQryAnnex; id shown per product request (Access had it hidden)
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'ID', kind: 'number', align: 'right' },
  { key: 'amendmentNo', label: 'Amendment No.' },
  { key: 'amendmentDate', label: 'Amendment Date.', kind: 'date' },
  { key: 'contractNo', label: 'Contract Ref' },
  { key: 'clientCode', label: 'Supplier Code' },
  { key: 'clientName', label: 'Supplier' },
  { key: 'contentBeforeChange', label: 'Before change' },
  { key: 'contentAfterChange', label: 'After change' },
  { key: 'note', label: 'Note' },
  { key: 'price', label: 'Price', kind: 'number', align: 'right' },
  { key: 'quantity', label: 'Quantity', kind: 'number', align: 'right' },
  { key: 'dateUpdate', label: 'Updated Date', kind: 'datetime' },
  { key: 'personUpdate', label: 'Updated Person' },
]

const NON_SUM_TOTAL_KEYS = new Set<keyof SaleAmendment>(['id'])
const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  return `${day}-${month}-${year}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleAmendment[], key: keyof SaleAmendment): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

export default function SaleAmendmentListView() {
  const { canEdit } = usePagePermission()
  const {
    selectedId: selectedKey,
    setSelectedId: setSelectedKey,
    toggleSelect: toggleSelectKey,
  } = useRowSelection<string>()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleAmendment> | null>(null)
  const { getWidth, startResize } = useColumnWidths('sale-contract.amendment')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleAmendment
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'new' | 'edit'>('new')
  const [printLang, setPrintLang] = useState<'en' | 'vn' | null>(null)
  const [contractDraft, setContractDraft] = useState<SaleContractPayload | null>(null)
  const [contractDialogOpen, setContractDialogOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_contract',
    prefKey: 'columns',
    subKey: 'amendment',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-contract.amendment',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const { data: amendmentList, isLoading, isFetching, error } = useGetSaleAmendmentsQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
    },
    // Mỗi lần đổi tab Sales Contract → component remount → refetch lại data.
    { refetchOnMountOrArgChange: true },
  )
  const [cancelAmendment, { isLoading: isCancelling }] = useCancelSaleAmendmentMutation()
  const [separateContract, { isLoading: isSeparating }] = useSeparateSaleAmendmentContractMutation()

  const baseRows = useMemo(() => amendmentList?.items ?? [], [amendmentList])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])

  // Filter bar derived
  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  // Per-column popover meta
  const popoverColumn = useMemo(
    () => (colPopover ? TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null : null),
    [colPopover],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, String(colPopover.key))
        : undefined,
    [popoverKind, colPopover, baseRows],
  )
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const selectedRow = useMemo(
    () => filteredRows.find((row) => row.amendmentNo === selectedKey) ?? null,
    [filteredRows, selectedKey],
  )
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && !NON_SUM_TOTAL_KEYS.has(col.key))
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const idRangeText = useMemo(() => {
    const ids = rows.map((r) => Number(r.id)).filter(Number.isFinite)
    if (!ids.length) return 'ID: —'
    return `ID: ${Math.min(...ids)}-${Math.max(...ids)}`
  }, [rows])
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const minTableWidth = Math.max(1200, visibleColumns.length * 96)

  const toggleSort = (key: keyof SaleAmendment) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const openForm = (mode: 'new' | 'edit') => {
    if (mode === 'edit' && !selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    setFormMode(mode)
    setFormOpen(true)
  }

  const handleDeleteClick = () => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedRow) return
    setDeleteConfirmOpen(false)
    try {
      await cancelAmendment(selectedRow.amendmentNo).unwrap()
      setSelectedKey(null)
    } catch {
      setActionError('Failed to delete amendment.')
    }
  }

  const handleContractSeparation = async () => {
    if (!selectedRow) {
      window.alert('Please choose a record!')
      return
    }
    try {
      const result = await separateContract(selectedRow.amendmentNo).unwrap()
      setContractDraft({
        ...result.draft,
        ...(result.brokerFirm ? { brokerFirm: result.brokerFirm } : {}),
        ...(result.traderName ? { traderName: result.traderName } : {}),
      } as typeof result.draft)
      setContractDialogOpen(true)
    } catch {
      setActionError('Failed to prefill separated contract.')
    }
  }

  const cellValue = (row: SaleAmendment, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    return String(val)
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => openForm('new')}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openForm('edit')}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="error"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || isCancelling || !canEdit}
              onClick={handleDeleteClick}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              title="Delete"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={() => setPrintLang('en')}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print EN"
            >
              {/* "EN"/"VN" luôn show (kể cả xs) để phân biệt 2 nút Print khác lang. */}
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print{' '}</Box>
              EN
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow}
              onClick={() => setPrintLang('vn')}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print VN"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print{' '}</Box>
              VN
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || isSeparating || !canEdit}
              onClick={handleContractSeparation}
              startIcon={<CallSplitIcon sx={{ fontSize: 16 }} />}
              title="Contract Separation"
            >
              {/* sm: "Separate" — md+: "Contract Separation" */}
              <Box component="span" sx={SHOW_ON_TABLET_INLINE}>Separate</Box>
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Contract Separation</Box>
            </Button>
          </Stack>
        }
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>
      <Divider sx={{ mb: 0.5 }} />
      {actionError ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>
        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => {
                setSearchTerm(v)
                setPage(0)
              }}
              placeholder="Amendment no, contract no, client..."
              label="Search amendments"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Sale Amendments"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading && baseRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Sale Amendments found"
              description="Try adjusting the table filters above."
            />
          ) : (
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {visibleColumns.map((col) => {
                  const w = getWidth(col.key as string)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className="app-table-header-cell"
                      onClick={() => toggleSort(col.key)}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', position: 'relative' }}
                    >
                      {col.label}
                      {getTableSortSuffix(sortState, col.key)}
                      <ColumnFilterIcon
                        active={columnFilters.isActive(String(col.key))}
                        onClick={(position) => setColPopover({ key: col.key, position })}
                        ariaLabel={`Filter ${col.label}`}
                      />
                      <ResizeHandle onMouseDown={(e) => startResize(col.key as string, e)} />
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            fixedFooterContent={() => (
              <TableRow>
                {visibleColumns.map((col, index) => {
                  const total = totalRowValues.get(col.key)
                  const w = getWidth(col.key as string)
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ fontWeight: 700, whiteSpace: 'nowrap', py: 0.5, bgcolor: 'background.paper', borderTop: (theme) => `1px solid ${theme.palette.divider}`, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                      {index === 0 ? 'Total' : total == null ? '' : formatAccessNumber(total)}
                    </TableCell>
                  )
                })}
              </TableRow>
            )}
            itemContent={(_: number, row: SaleAmendment) =>
              visibleColumns.map((col) => {
                const val = cellValue(row, col)
                const w = getWidth(col.key as string)
                return (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                    sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {val || '-'}
                    </Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const rowKey = Number.isFinite(idx) ? rows[idx]?.amendmentNo : null
                return (
                  <TableRow
                    {...props}
                    hover
                    selected={rowKey != null && rowKey === selectedKey}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (rowKey != null) toggleSelectKey(rowKey)
                    }}
                  />
                )
              },
            }}
          />
          )}
        </Box>
      </Box>
      <SaleAmendmentFormDialog
        open={formOpen}
        mode={formMode}
        amendmentNo={selectedRow?.amendmentNo}
        onClose={() => setFormOpen(false)}
      />
      <SaleAmendmentPrintDialog
        open={printLang !== null}
        language={printLang ?? 'en'}
        amendment={selectedRow}
        onClose={() => setPrintLang(null)}
      />
      <ContractFormDialog
        open={contractDialogOpen}
        onClose={() => setContractDialogOpen(false)}
        mode="new"
        initialDraft={contractDraft}
      />

      {/* ── Delete confirmation dialog ── */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1rem', color: 'error.main' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Do you want to delete this annex no =&nbsp;
            <strong>{selectedRow?.amendmentNo}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, gap: 0.5 }}>
          <Button size="small" variant="outlined" onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button size="small" variant="contained" color="error" onClick={handleDeleteConfirm} disabled={isCancelling}>
            {isCancelling ? <CircularProgress size={14} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
